import React, { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, Star } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface ProfileCompletenessProps {
  showRecommendations?: boolean;
  className?: string;
}

export const ProfileCompleteness: React.FC<ProfileCompletenessProps> = ({ 
  showRecommendations = true, 
  className = "" 
}) => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  if (!profile) return null;

  // Calculate profile completeness based on key fields
  const calculateCompleteness = () => {
    const fields = [
      { key: 'full_name', weight: 15, label: 'Full Name' },
      { key: 'bio', weight: 15, label: 'Bio/Summary' },
      { key: 'location', weight: 10, label: 'Location' },
      { key: 'skills', weight: 20, label: 'Skills (3+)', value: profile.skills?.length >= 3 },
      { key: 'key_skills', weight: 15, label: 'Key Skills (2+)', value: profile.key_skills?.length >= 2 },
      { key: 'experience_years', weight: 10, label: 'Experience Years', value: profile.experience_years > 0 },
      { key: 'cv_url', weight: 10, label: 'CV/Resume Upload' },
      { key: 'phone_verified', weight: 5, label: 'Phone Number Verified', value: profile.phone_verified === true },
    ];

    let totalScore = 0;
    let maxScore = 0;
    const missing = [];
    const completed = [];

    fields.forEach(field => {
      maxScore += field.weight;
      let isComplete = false;

      if (field.value !== undefined) {
        isComplete = field.value;
      } else {
        const value = profile[field.key as keyof typeof profile];
        isComplete = value && (
          typeof value === 'string' ? value.trim().length > 0 :
          Array.isArray(value) ? value.length > 0 :
          value !== null && value !== undefined
        );
      }

      if (isComplete) {
        totalScore += field.weight;
        completed.push(field.label);
      } else {
        missing.push(field.label);
      }
    });

    return {
      percentage: Math.round((totalScore / maxScore) * 100),
      missing,
      completed,
      canShowRecommendations: totalScore >= 60
    };
  };

  // Force re-render when profile changes by using profile as dependency
  const completeness = React.useMemo(() => calculateCompleteness(), [profile]);

  // Map field keys to their HTML element IDs for navigation
  const getFieldElementId = (fieldLabel: string): string => {
    const fieldMap: Record<string, string> = {
      'Full Name': 'fullName',
      'Bio/Summary': 'bio', 
      'Location': 'location',
      'Skills (3+)': 'skills-section',
      'Key Skills (2+)': 'key-skills-section',
      'Experience Years': 'experience',
      'CV/Resume Upload': 'cvFile',
      'Phone Number Verified': 'phoneNumber'
    };
    return fieldMap[fieldLabel] || '';
  };

  // Navigate to specific field and highlight it
  const navigateToField = (fieldLabel: string) => {
    const elementId = getFieldElementId(fieldLabel);
    if (elementId) {
      navigate(`/account-settings?tab=profile&highlight=${elementId}`);
    } else {
      navigate('/account-settings?tab=profile');
    }
  };

  // Handle highlighting when arriving with highlight parameter
  useEffect(() => {
    const highlightFieldId = searchParams.get('highlight');
    if (highlightFieldId) {
      const timer = setTimeout(() => {
        const element = document.getElementById(highlightFieldId);
        if (element) {
          // Scroll to element
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Add highlight class
          element.classList.add('ring-2', 'ring-yellow-400', 'ring-offset-2', 'transition-all', 'duration-500');
          
          // Remove highlight after 3 seconds
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-yellow-400', 'ring-offset-2', 'transition-all', 'duration-500');
          }, 3000);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const canShowRecommendations = completeness.percentage >= 60;

  const getStatusColor = () => {
    if (completeness.percentage >= 80) return "text-green-600";
    if (completeness.percentage >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getStatusIcon = () => {
    if (completeness.percentage >= 60) {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
    return <AlertCircle className="w-4 h-4 text-yellow-600" />;
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <span>Profile Completeness</span>
          </div>
          <Badge variant={canShowRecommendations ? "default" : "secondary"}>
            {completeness.percentage}%
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left side - Progress and Actions */}
          <div className="lg:col-span-2 space-y-4">
            <div className="space-y-2">
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-primary/20">
                <div 
                  className="h-full bg-secondary transition-all"
                  style={{ width: `${completeness.percentage}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span className="text-secondary">{completeness.completed.length} completed</span>
                <span className="text-primary">{completeness.missing.length} remaining</span>
              </div>
            </div>

            {canShowRecommendations && showRecommendations && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">
                    Recommendations Unlocked!
                  </span>
                </div>
                <p className="text-xs text-green-700 mb-2">
                  Your profile is complete enough to receive personalized job recommendations.
                </p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-green-300 text-green-700 hover:bg-green-100"
                  onClick={() => navigate('/jobs?ai=true')}
                >
                  Get AI Recommendations
                </Button>
              </div>
            )}

            {!canShowRecommendations && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    Complete to unlock recommendations
                  </span>
                </div>
                <p className="text-xs text-yellow-700 mb-2">
                  Add more details to reach 60% and unlock personalized job recommendations.
                </p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-yellow-300 text-yellow-700 hover:bg-yellow-100"
                  onClick={() => navigateToField(completeness.missing[0])}
                >
                  Complete Next Field
                </Button>
              </div>
            )}
          </div>

          {/* Right side - Outstanding Items */}
          {completeness.missing.length > 0 && (
            <div className="lg:col-span-1">
              <div className="bg-muted/50 border rounded-lg p-3">
                <h4 className="text-sm font-medium text-foreground mb-3">
                  Outstanding Items ({completeness.missing.length})
                </h4>
                <div className="space-y-2">
                  {completeness.missing.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => navigateToField(item)}
                      className="flex items-center justify-between w-full p-2 text-xs text-left bg-background/50 hover:bg-background border border-border/50 hover:border-border rounded transition-all"
                    >
                      <span className="text-muted-foreground">{item}</span>
                      <AlertCircle className="w-3 h-3 text-muted-foreground/60" />
                    </button>
                  ))}
                </div>
                {completeness.percentage < 100 && (
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <p className="text-xs text-muted-foreground">
                      Click any item above to complete it
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileCompleteness;